// Message templates & playbook content for Main Street Sales Console.
// All templates are generated dynamically from lead fields.
// {owner} falls back to "there" when the owner name is missing.

function ownerFirstName(owner) {
  if (!owner) return "there";
  // Take the first word (first name), strip punctuation.
  const first = String(owner).trim().split(/\s+/)[0];
  return first || "there";
}

function reviewQuoteFragment(best_review) {
  if (!best_review || !String(best_review).trim()) return "";
  const clean = String(best_review).trim().replace(/\s+/g, " ");
  return ` One review put it well: "${clean}"`;
}

export function buildWarmEmail(lead, repName) {
  const owner = ownerFirstName(lead.owner);
  const review_quote = reviewQuoteFragment(lead.best_review);
  const sample_url = lead.sample_url || "[sample pending — build one before sending]";
  return `Hi ${owner},

I run a small studio here in Florida, and I do something a little backwards: when I find a local business that's clearly great but has no website, I build them one first, then reach out.

${lead.name} came up - ${lead.reviews} reviews at ${lead.rating} stars, and not one lukewarm.${review_quote} A reputation like that deserves a real home online. Right now anyone who Googles you and doesn't already have your number has nowhere to land.

So here's the one I built for you - nothing to sign up for, just take a look:
${sample_url}

If you like it, let's hop on a quick call - I'll walk you through it and we'll dial in your colors, add your logo, and get it live on your own domain this week.

${repName}`;
}

export function buildShortEmail(lead, repName) {
  const owner = ownerFirstName(lead.owner);
  const sample_url = lead.sample_url || "[sample pending — build one before sending]";
  return `Hi ${owner} - I build websites for local businesses and I already made one for ${lead.name}. You've got ${lead.reviews} five-star reviews and no site, which is quietly costing you the customers who look you up. Take a look - if it's close, one quick call and we'll customize it to your brand and get you live.
${sample_url}
- ${repName}`;
}

export function buildSms(lead, repName) {
  const owner = ownerFirstName(lead.owner);
  const sample_url = lead.sample_url || "[sample pending — build one before sending]";
  return `Hi ${owner}, it's ${repName} - I built ${lead.name} a quick website to show you (${lead.reviews} 5-star reviews and no site). Have a look - if you like it we'll jump on a quick call to make it yours and get it live: ${sample_url}`;
}

export function emailSubjectFor(lead) {
  return `A website for ${lead.name}`;
}

// -------------------------- Playbook content --------------------------

export const PLAYBOOK_STAGES = [
  {
    n: 1,
    title: "Opener (cold call)",
    kind: "Lead with a GIFT, not a pitch. Reciprocity flips the call.",
    script:
      "Hey, is this {owner}? I'll be quick - I actually already built {business} a website and wanted to show it to you. You've got {reviews} five-star reviews and no site, so you're basically invisible to the people who Google you. Can I text you the link right now while we're talking?",
  },
  {
    n: 2,
    title: "Past the gatekeeper",
    kind: "Non-salesy, low-friction ask.",
    script:
      "Hi - I'm not selling anything, I actually built something for the owner and just need to get it in front of them. Is {owner} around, or a better cell to text it to?",
  },
  {
    n: 3,
    title: "They opened the sample",
    kind: "Highlight the real reviews + click-to-call. Then stop talking.",
    script:
      "So that's your real Google reviews pulled in live, your photos, click-to-call - it works on a phone right now. I can put it on your own domain this week. What do you think when you see it?",
  },
  {
    n: 4,
    title: "Pricing",
    kind: "Anchor on value before the number. Setup covers a real customization round; monthly quietly includes GBP + reviews management — that's what makes $299 worth it.",
    script:
      "$1,499 to launch it on your domain - that's the setup: we swap in your brand colors, add your logo, real services, hours, service-area, photos, click-to-call - one full customization round so it's yours, not a template. Then $299/month for security, maintenance & hosting, 2-3 small edits a month, and we quietly manage your Google Business Profile and reviews - that's what pulls the jobs in. But look at it first - it's already built with your name on it.",
  },
  {
    n: 5,
    title: "\"I don't need a website\"",
    kind: "Frame inaction as ongoing lost customers.",
    script:
      "Totally get it. Here's the thing: {reviews} people already vouched for you online. When the next one tells a friend, that friend Googles you and finds nothing, or a competitor. The site catches the customers your reputation already earned.",
  },
  {
    n: 6,
    title: "\"Too expensive / not now\"",
    kind: "Reframe cost against one extra job. Point at the ongoing value.",
    script:
      "Fair. One extra driveway a month more than covers the $299 - and that's not just hosting, it's us actively working your Google profile and reviews. The $1,499 setup is a one-time flip to make it yours and live. Want me to hold it on your domain for 30 days while you think?",
  },
  {
    n: 7,
    title: "\"Let me think about it\"",
    kind: "Isolate the real objection.",
    script:
      "Of course - the link stays live. Quick one: is it the site itself, or the timing?",
  },
  {
    n: 8,
    title: "Close",
    kind: "Assume the sale. Offer two yeses.",
    script:
      "Here's what I'll do: I'll point it at your domain and you're live this week. $1,499 to start, then $299/month. Card on file, or should I send a secure link?",
  },
  {
    n: 9,
    title: "Follow-up (no answer)",
    kind: "Soft, real scarcity.",
    script:
      "Hey {owner}, {rep_name} here - your site's still live at the link. Had a couple other {city} folks ask about the same spot this week, wanted to check before I cycle it. Worth a 2-min look?",
  },
];

export const EMAIL_PSYCHOLOGY = [
  { k: "Reciprocity", v: "you gave something before asking." },
  { k: "Social proof, mirrored", v: "quote THEIR review + real star count." },
  { k: "Loss aversion", v: "\"nowhere to land\" frames inaction as ongoing lost customers." },
  { k: "Specificity = truth", v: "real numbers + real name read as human research." },
  { k: "Endowment", v: "\"yours to keep\" makes giving it up feel like a loss." },
  { k: "Zero friction", v: "the only ask is \"take a look.\"" },
];

export const STATUS_ORDER = [
  "new",
  "emailed",
  "called",
  "interested",
  "call_booked",
  "customizing",
  "closed_won",
  "dead",
];

export const STATUS_LABELS = {
  new: "New",
  emailed: "Emailed",
  called: "Called",
  interested: "Interested",
  call_booked: "Call booked",
  customizing: "Customizing",
  closed_won: "Closed-Won",
  dead: "Dead",
};

// -------------------------- Pricing anchor --------------------------

export const PRICING = {
  setup: {
    amount: "$1,499",
    label: "One-time setup",
    includes:
      "Build + one full \"make it yours\" customization round: brand colors, logo, real services, hours, service-area, photo swaps, contact + click-to-call.",
  },
  monthly: {
    amount: "$299",
    label: "per month",
    includes:
      "Security, maintenance & hosting. Includes 2-3 small edit requests per month. Quietly includes managing the Google Business Profile + reviews — that's what makes $299 worth it.",
  },
};

// -------------------------- Edits policy --------------------------

export const EDITS_POLICY = [
  "Setup includes one full \"make it yours\" revision round (colors, logo, real copy, photo swaps, hours, service-area, contact).",
  "Monthly ($299) includes ~2-3 small edit requests per month — quick copy tweaks, photo swaps, seasonal notices, hours changes.",
  "Anything bigger — a new page, a booking flow, a payments integration, a fresh design pass — is a paid add-on. Quote it before doing it.",
];

// -------------------------- Upsells (post-close) --------------------------

export const UPSELLS = [
  {
    name: "Additional page",
    price: "$249 each",
    note: "Bundle: 3 pages for $599",
    lead: false,
  },
  {
    name: "Online booking / scheduling",
    price: "$499 setup + $49/mo",
    note: "Great for service businesses that take appointments.",
    lead: false,
  },
  {
    name: "Online payments / deposits",
    price: "$499 setup",
    note: "Card-on-file, deposits, invoicing links.",
    lead: false,
  },
  {
    name: "Blog / content writing",
    price: "$349/mo (4 SEO posts)",
    note: "Or $119/post à la carte. LEAD with this one — it's the highest-value nurture upsell.",
    lead: true,
  },
  {
    name: "Extra location",
    price: "$399 setup + $99/mo",
    note: "Per additional location. Includes its own local landing page + GBP.",
    lead: false,
  },
];

export const NURTURE_NOTE =
  "Sell these AFTER they're a happy client, not in the first close. Wait until the site is live and they've said something positive — then market these over time. Lead with content writing; it's the highest-value monthly and the easiest re-close.";
